import { Control } from "Controls/Control";
import { IButtonControl, IControl } from "Controls/Interfaces";
import { GridControlTypes, DisplayModes, CssClass, NotificationTypes } from "Enums";
import { IDataRow, IExecuteResult } from "Entities";
import { IControlDefinition } from "Grids/Interfaces";
import { CommandService, TextBlockService } from "Service";
import { Navigation, CoreContext } from "Core";
import { EventArgs, EventObject } from "Core/Events";
import { ControlTemplate } from "Controls/Entities";
import { ControlEventTypes } from "Core/Events/EventTypes";

/**
 * ## ButtonControl
 *
 * Control for executing commands, scripts and links on button click.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a ButtonControl with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 * @module Controls
 */
export class ButtonControl extends Control implements IButtonControl {
    private _kendoElement: kendo.ui.Button;
    private commandService: CommandService;

    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-button";
    /**
     * @hidden
     */
    protected defaultEditorElement: string = `<button type="button" class="k-button" />`;

    /**
     * Create new ButtonControl.
     * @param controlDefinition
     * @hidden
     */
    constructor(
        controlDefinition: IControlDefinition,
        textBlockCategory: string,
        textBlockService: TextBlockService,
        commandService: CommandService
    ) {
        super(controlDefinition, textBlockCategory, textBlockService);
        this.commandService = commandService;
    }

    /**
     * Executed on button click when button has a command.
     * @param e EventArgs.
     */
    private async onCommand(e: EventArgs<IControl>): Promise<void> {
        try {
            // 0. trigger before command, so the grid can show busyindicator or whatever.
            this.trigger(ControlEventTypes.BeforeCommand, e);

            // 1. check required fields on context
            this.trigger(ControlEventTypes.CheckRequiredFields, e);

            if (await this.checkCommandCancel(e)) {
                return;
            }

            // 2. show confirmtext and confirm
            if (this.controlDefinition.ConfirmText) {
                let text = this.textBlockService.get(this.controlDefinition.ConfirmText, this.textBlockCategory);

                let execute = await Navigation.confirmAsync(
                    this.resolve(text),
                    this.textBlockService.get("pageTitle", this.textBlockCategory)
                );

                if (!execute) {
                    e.preventDefault();
                    this.trigger(ControlEventTypes.AfterCommandCancel, e);
                    return;
                }
            }

            // 3. trigger before execute scripts on context
            this.trigger(ControlEventTypes.BeforeExecuteContext, e);
            if (await this.checkCommandCancel(e)) {
                return;
            }

            // 4. trigger before execute of button
            if (this.controlDefinition.BeforeExecuteScript) {
                this.trigger(ControlEventTypes.BeforeExecuteControl, e);
                if (await this.checkCommandCancel(e)) {
                    return;
                }
            }

            let executeResult: IExecuteResult = null;
            // 5. execute command
            if (this.getControlType() == GridControlTypes.MultiPageUpdate) {
                let changedDataRows = this.execute<IDataRow[], {}>(ControlEventTypes.GetChangedDataRows, null);
                if (changedDataRows.length > 0) {
                    executeResult = await this.commandService.setMultiple(
                        this.controlDefinition.GridID,
                        this.controlDefinition.ControlID,
                        changedDataRows
                    );
                } else {
                    Navigation.notify(this.textBlockService.get("EasyEditNoChangesHint", this.textBlockCategory), NotificationTypes.Info);
                    e.preventDefault();
                    this.trigger(ControlEventTypes.AfterCommandCancel, e);
                    return;
                }
            } else if (this.getControlType() == GridControlTypes.DatasetCommand) {
                let commandOption = this.execute<{}, {}>(ControlEventTypes.GetCommandOption);
                executeResult = await this.commandService.setDataset(
                    this.controlDefinition.GridID,
                    this.controlDefinition.ControlID,
                    commandOption
                );
            } else {
                let dataRow = this.execute<IDataRow, {}>(ControlEventTypes.GetDataRow);
                executeResult = await this.commandService.set(this.controlDefinition.GridID, this.controlDefinition.ControlID, dataRow);
            }
            if (executeResult && executeResult.error) {
                e.preventDefault();
                this.trigger(ControlEventTypes.AfterCommandCancel, e);
                return;
            }

            // set data to outputParameters
            if (executeResult && executeResult.outputParameters) {
                if (!e.data) {
                    e.data = {};
                }
                e.data.outputParameters = [];
                if (this.controlDefinition.AdditionalOutputParameter1Name) {
                    e.data.outputParameters[0] = executeResult.outputParameters[this.controlDefinition.AdditionalOutputParameter1Name];
                }
                if (this.controlDefinition.AdditionalOutputParameter2Name) {
                    e.data.outputParameters[1] = executeResult.outputParameters[this.controlDefinition.AdditionalOutputParameter2Name];
                }
            }

            // 6. trigger after execute scripts on context
            this.trigger(ControlEventTypes.AfterExecuteContext, e);
            if (await this.checkCommandCancel(e)) {
                return;
            }

            // 7. trigger after execute of button
            if (this.controlDefinition.AfterExecuteScript) {
                this.trigger(ControlEventTypes.AfterExecuteControl, e);
                if (await this.checkCommandCancel(e)) {
                    return;
                }
            }

            // 8. Notify Grids that the Command Execution is finished.
            this.trigger(ControlEventTypes.AfterCommand, e);
        } catch (error) {
            console.log("error: " + error);
        }
    }

    /**
     * Trigger the click event on this button.
     * @since 4.0.0
     */
    public click(): void {
        if (this.jQueryElement) {
            this.jQueryElement.click();
        }
    }

    /**
     * Executes the click handler.
     */
    private onClick(e: EventArgs<IControl>): void {
        if (this.controlDefinition.CheckRequiredFields) {
            this.trigger(ControlEventTypes.CheckRequiredFields, e);
            if (e.isDefaultPrevented()) {
                return;
            }
        }

        if (this.controlDefinition.Script) {
            this.eventHandler.executeScript(this.resolve(this.controlDefinition.Script), e);
        } else if (this.controlDefinition.Link) {
            let link = this.resolve(this.controlDefinition.Link) || this.controlDefinition.Link;
            if (this.controlDefinition.LinkMode) {
                Navigation.openWindow(link, this.controlDefinition.LinkMode);
            } else {
                Navigation.navigate(link);
            }
        }
    }

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        if (this.controlDefinition.Caption) {
            this.jQueryElement.append(this.textBlockService.get(this.controlDefinition.Caption, this.textBlockCategory));
        }

        if (this.controlDefinition.CommandID) {
            this.on(ControlEventTypes.Click, e => this.onCommand(e));
            this.off(ControlEventTypes.BeforeExecuteControl); // before execute of command buttons will not be executed in EditContext.
            this.off(ControlEventTypes.AfterExecuteControl); // after execute of command buttons will not be executed in EditContext.
            if (this.controlDefinition.BeforeExecuteScript) {
                this.eventHandler.on(
                    ControlEventTypes.BeforeExecuteControl,
                    new EventObject(this.controlDefinition.BeforeExecuteScript, (sender, args) => this == args.sender, true)
                );
            }
            if (this.controlDefinition.AfterExecuteScript) {
                this.eventHandler.on(
                    ControlEventTypes.AfterExecuteControl,
                    new EventObject(this.controlDefinition.AfterExecuteScript, (sender, args) => this == args.sender, true)
                );
            }
        } else {
            this.on(ControlEventTypes.Click, e => this.onClick(e));
        }

        this.jQueryElement.on("focusout", e => this.trigger(ControlEventTypes.LostFocus));
        this.jQueryElement.on("click", () => this.trigger(ControlEventTypes.Click));
    }

    /**
     * Get the button value.
     * @hidden
     */
    public getValue(): string {
        return this.jQueryElement.val() as string;
    }

    /**
     * Set the button value.
     * @param value Value to be set.
     * @hidden
     */
    public setValue(value: string) {
        this.jQueryElement.val(value);
    }

    /**
     * Check if the command was cancelled.
     * @param e EventArgs.
     */
    private async checkCommandCancel(e: EventArgs<IControl>): Promise<boolean> {
        try {
            let result = false;
            result = await e.defaultPreventedPromise;
            if (!result) {
                result = e.isDefaultPrevented();
            }
            if (result) {
                this.trigger(ControlEventTypes.AfterCommandCancel, e);
            }
            return result;
        } catch (error) {
            console.log("error: " + error);
        }
    }

    /**
     * Internal method for getting the template.
     * @param displayMode DisplayMode for control.
     * @param bindingField Field which the control is bound to.
     * @hidden
     */
    protected getTemplateInternal(displayMode: DisplayModes, bindingField: string): ControlTemplate {
        if (displayMode == DisplayModes.Display) {
            if (
                this.getControlType() == GridControlTypes.Add ||
                this.getControlType() == GridControlTypes.Update ||
                this.getControlType() == GridControlTypes.Cancel
            ) {
                return null;
            }
        }

        if (displayMode == DisplayModes.Add) {
            if (
                this.getControlType() == GridControlTypes.Update ||
                this.getControlType() == GridControlTypes.Edit ||
                this.getControlType() == GridControlTypes.Delete
            ) {
                return null;
            }
        }

        if (displayMode == DisplayModes.Edit) {
            if (
                this.getControlType() == GridControlTypes.Add ||
                this.getControlType() == GridControlTypes.Edit ||
                this.getControlType() == GridControlTypes.Delete
            ) {
                return null;
            }
        }

        let elem = $(this.defaultEditorElement);

        super.setDefaultEditorProperties(elem);

        let cssClass: string = "";
        switch (this.controlDefinition.ControlTypeID) {
            case GridControlTypes.Add:
                cssClass = CssClass.add;
                break;
            case GridControlTypes.Delete:
                cssClass = CssClass.delete;
                break;
            case GridControlTypes.Edit:
                cssClass = CssClass.edit;
                break;
            case GridControlTypes.Update:
                cssClass = CssClass.update;
                break;
            case GridControlTypes.Cancel:
                cssClass = CssClass.cancel;
                break;
            case GridControlTypes.MultiPageUpdate:
                cssClass = CssClass.multipageupdate;
                break;
            case GridControlTypes.Command:
                cssClass = CssClass.command;
                break;
            case GridControlTypes.DatasetCommand:
                cssClass = CssClass.datasetcommand;
                break;
        }

        elem.addClass(cssClass);

        if (
            this.controlDefinition.IsSubmit ||
            this.controlDefinition.ControlTypeID == GridControlTypes.MultiPageUpdate ||
            this.controlDefinition.ControlTypeID == GridControlTypes.DatasetCommand
        ) {
            elem.addClass("k-primary");
        }

        // image button
        if (this.controlDefinition.Icon && this.controlDefinition.Icon.indexOf(".") >= 0) {
            elem.addClass("k-button-icon");
            let img = $("<img/>");
            img.attr("type", "image")
                .attr("style", "width: 15px; height: 15px;")
                .attr("src", CoreContext.Environment.ImagePath + this.controlDefinition.Icon);
            elem.append(img);
        }
        // sprite button
        else if (this.controlDefinition.Icon) {
            let span = $("<span/>");
            span.addClass(this.controlDefinition.Icon);
            if (this.controlDefinition.Caption) {
                elem.addClass("k-button-icontext");
            } else {
                elem.addClass("k-button-icon");
            }

            elem.append(span);
        }

        let controlTemplate = new ControlTemplate();
        controlTemplate.template = elem[0].outerHTML;
        controlTemplate.element = elem;
        controlTemplate.editable = true;
        return controlTemplate;
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        if (this._kendoElement) {
            this._kendoElement.destroy();
        }
        if (this.jQueryElement) {
            this.jQueryElement.off("click");
            this.jQueryElement.off("focusout");
        }
    }
}
